import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Notification,
  NumberField,
  Panel,
  ProgressBar,
  Select,
  Table,
  TextField,
  useConfirm,
  useDialog,
  type DialogHandle,
  type TableColumn,
} from '@hgc/ui';
import { formatParamValue, type ParamValidationResult } from '@hgc/core';
import { useCallback, useEffect, useMemo, useState, type JSX, type MouseEvent } from 'react';

import {
  PARAM_ROW_LIMIT,
  describeSetStatus,
  type ParamRow,
  type ParamSessionSnapshot,
  type ParamWriteFailure,
  type ParameterSession,
} from './session';
import { useParameterSessionSnapshot } from './useParameterSessionSnapshot';

/**
 * 全量参数编辑器。
 *
 * 三件事在这个文件里是硬约束，改动时不要放松：
 *
 * 1. **绝不显示"已保存"，除非拿到了回读确认。** `PARAM_SET` 没有 ACK，飞控的回应就是
 *    再发一条 `PARAM_VALUE`。所以这里只在 `status === 'applied'`（收到同名回读且值相符）
 *    时才显示"已写入并回读确认"。失败要区分"没回应"和"回应了但值不同"，并且**给出实际值**。
 * 2. **元数据缺失不影响可用。** 拿不到 pdef 就是少说明、少范围提示（野外没网的常态），
 *    参数照常下载、照常读写。
 * 3. **危险参数的写入必须过一次确认。** 确认弹窗写清"从什么改到什么、会影响什么"，
 *    取消就什么都不做（而不是"写一半"）。
 */

export interface ParameterEditorProps {
  readonly session: ParameterSession;
}

const rowKey = (row: ParamRow): string => row.name;

const NOTICE_TEXT = 'text-xs';

export function ParameterEditor({ session }: ParameterEditorProps): JSX.Element {
  const confirm = useConfirm();
  const dialog = useDialog();
  const snapshot = useParameterSessionSnapshot(session);

  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [onlyModified, setOnlyModified] = useState(false);
  const [busyName, setBusyName] = useState<string | null>(null);
  const [busy, setBusy] = useState<'idle' | 'download' | 'apply'>('idle');

  // 打开参数页就发起一次读取（会话会记住，切回来不会重下）。全量读不完也不影响已到的部分
  useEffect(() => {
    void session.ensureDownloaded();
  }, [session]);

  const failureByName = useMemo(() => {
    const map = new Map<string, ParamWriteFailure>();
    for (const failure of snapshot.failures) map.set(failure.name, failure);
    return map;
  }, [snapshot.failures]);

  const visibleRows = useMemo(
    () => filterRows(snapshot, search, group, onlyModified),
    [snapshot, search, group, onlyModified],
  );
  const shownRows = useMemo(
    () => (visibleRows.length > PARAM_ROW_LIMIT ? visibleRows.slice(0, PARAM_ROW_LIMIT) : visibleRows),
    [visibleRows],
  );

  const validate = useCallback(
    (name: string, value: number): ParamValidationResult => session.validate(name, value),
    [session],
  );

  const handleEdit = useCallback(
    (name: string, value: number | null) => {
      session.setPending(name, value);
    },
    [session],
  );

  const handleWrite = useCallback(
    async (row: ParamRow, event: MouseEvent<HTMLButtonElement>) => {
      const pending = row.pending;
      if (pending === null) return;

      if (row.dangerous) {
        const accepted = await confirm({
          title: `写入危险参数 ${row.name}？`,
          consequence:
            `会把 ${row.name} 从 ${text(pending.previous)} 改成 ${text(pending.requested)}，` +
            `并立即作用于飞控。${row.dangerReason ?? '这个参数会影响飞行行为'}。` +
            '写入是否成功只能靠飞控回读确认。',
          confirmLabel: `写入 ${row.name}`,
          danger: 'warning',
          anchor: event.currentTarget,
        });
        if (!accepted) return;
      }

      setBusyName(row.name);
      try {
        await session.write(row.name);
      } finally {
        setBusyName(null);
      }
    },
    [confirm, session],
  );

  const handleApplyAll = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      const dangerous = snapshot.pending.filter((edit) => session.isDangerous(edit.name));
      if (dangerous.length > 0) {
        const accepted = await confirm({
          title: `写回 ${snapshot.pending.length} 项改动（含 ${dangerous.length} 项危险参数）？`,
          consequence:
            `其中 ${dangerous.length} 项会立即改变飞行行为：${dangerous
              .map((edit) => edit.name)
              .join('、')}。` +
            '全部改动会逐条写入并等待回读确认，未确认的会留在"待写入"里。',
          confirmLabel: '写入全部改动',
          danger: 'warning',
          detail: <PendingDetailTable session={session} />,
          anchor: event.currentTarget,
        });
        if (!accepted) return;
      }

      setBusy('apply');
      try {
        await session.applyAll();
      } finally {
        setBusy('idle');
      }
    },
    [confirm, session, snapshot.pending],
  );

  const handleReload = useCallback(async () => {
    setBusy('download');
    try {
      await session.reload();
    } finally {
      setBusy('idle');
    }
  }, [session]);

  const openDetail = useCallback(() => {
    /*
     * `let` + 事后赋值是刻意的：`open()` 是同步的，它返回的句柄要等到
     * JSX 求值之后才有值，而"用弹窗里的按钮关掉自己"必须拿到它。
     * 这里没有 TDZ 问题 —— 箭头函数体要等到用户点击时才执行，那时 `handle` 早就赋好了。
     * 换成 `const handle = dialog.open({ content: <... onClick={() => handle.close()} /> })`
     * 会直接抛 "Cannot access before initialization"。
     */
    let handle: DialogHandle | null = null;
    handle = dialog.open({
      title: `待写入改动（${snapshot.pending.length} 项）`,
      content: (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-xs text-fg-muted">
            这些改动还没有写进飞控。写入后只有收到回读且值相符才算成功，未确认的会留在这里。
          </p>
          <PendingDetailTable session={session} />
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => handle?.close()}>
              关闭
            </Button>
          </div>
        </div>
      ),
    });
  }, [dialog, session, snapshot.pending.length]);

  const download = snapshot.download;
  const downloading =
    download !== null && (download.phase === 'requesting' || download.phase === 'downloading' || download.phase === 'retrying');

  const columns = useMemo<readonly TableColumn<ParamRow>[]>(
    () => [
      {
        key: 'name',
        header: '参数',
        sortValue: (row) => row.name,
        width: '16rem',
        render: (row) => <span className="font-mono text-xs text-fg">{row.name}</span>,
      },
      {
        key: 'value',
        header: '机体当前值',
        align: 'end',
        sortValue: (row) => row.value,
        render: (row) => (
          <span className="tabular-nums text-fg">
            {text(row.value)}
            {row.units === null ? null : <span className="ml-1 text-[10px] text-fg-subtle">{row.units}</span>}
          </span>
        ),
      },
      {
        key: 'edit',
        header: '待写入值',
        align: 'end',
        render: (row) => (
          <ParamEditCell
            row={row}
            busy={busyName === row.name}
            validate={validate}
            onEdit={handleEdit}
            onRevert={(name) => session.revert(name)}
            onWrite={handleWrite}
          />
        ),
      },
      {
        key: 'state',
        header: '状态',
        render: (row) => (
          <ParamStateCell row={row} failure={failureByName.get(row.name) ?? null} />
        ),
      },
      {
        key: 'meta',
        header: '说明与取值范围',
        render: (row) => <ParamMetaCell row={row} />,
      },
    ],
    [busyName, failureByName, handleEdit, handleWrite, session, validate],
  );

  return (
    <div className="flex flex-col gap-3">
      <Panel
        title="参数编辑器"
        description={`共 ${snapshot.total} 条参数（机体自报 ${snapshot.expected ?? '未知'} 条）。改动先记在本地，写入后必须收到回读确认才算成功。`}
        actions={
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="secondary"
              loading={busy === 'download' || downloading}
              onClick={() => void handleReload()}
            >
              重新读取
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={snapshot.pending.length === 0}
              onClick={openDetail}
            >
              改动明细（{snapshot.pending.length}）
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy === 'apply'}
              disabled={snapshot.pending.length === 0}
              onClick={(event) => void handleApplyAll(event)}
            >
              写回全部改动
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={snapshot.pending.length === 0}
              onClick={() => session.revertAll()}
            >
              放弃全部改动
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-64">
              <TextField
                size="sm"
                value={search}
                onChange={setSearch}
                placeholder="按名称、显示名、说明或分组搜索"
                aria-label="搜索参数"
              />
            </div>

            <div className="w-48">
              <Select
                size="sm"
                value={group}
                onChange={setGroup}
                aria-label="按分组筛选"
                options={[
                  { value: '', label: `全部分组（${snapshot.total}）` },
                  ...snapshot.groups.map((item) => ({
                    value: item.id,
                    label: `${item.label}（${item.count}）`,
                  })),
                ]}
              />
            </div>

            <Checkbox
              value={onlyModified}
              onChange={setOnlyModified}
              label={`只看已修改（${snapshot.pending.length}）`}
              description={
                snapshot.referenceLabel === null
                  ? '尚未接入参考快照，所以这里只包含本次待写入的改动'
                  : `包含本次改动，以及与参考快照「${snapshot.referenceLabel}」不同的参数`
              }
            />

            <span className="ml-auto text-xs text-fg-muted">
              显示 {shownRows.length} / 匹配 {visibleRows.length} 条
            </span>
          </div>

          <MetadataNotice session={session} snapshot={snapshot} />

          {download === null ? null : (
            <DownloadNotice snapshot={snapshot} onCancel={() => session.cancelDownload()} />
          )}

          {snapshot.failures.length === 0 ? null : (
            <div className="flex flex-col gap-2">
              {snapshot.failures.map((failure) => (
                <FailureNotice
                  key={failure.name}
                  failure={failure}
                  onDismiss={() => session.dismissFailure(failure.name)}
                />
              ))}
              {snapshot.failures.length > 1 ? (
                <div>
                  <Button size="sm" variant="ghost" onClick={() => session.dismissFailures()}>
                    清除全部写入失败提示
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {snapshot.confirmed.length === 0 ? null : (
            <Notification
              level="ok"
              title={`已写入并回读确认（${snapshot.confirmed.length} 项）`}
              onDismiss={() => session.dismissConfirmations()}
              dismissLabel="关闭写入成功提示"
            >
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                {snapshot.confirmed.slice(-8).map((item) => (
                  <li key={item.name} className="tabular-nums">
                    {item.name} = {text(item.value)}
                  </li>
                ))}
              </ul>
            </Notification>
          )}

          {visibleRows.length > PARAM_ROW_LIMIT ? (
            <Notification level="info" title={`匹配到 ${visibleRows.length} 条，只显示前 ${PARAM_ROW_LIMIT} 条`}>
              上千行全部渲染会让低端设备卡住。请用搜索或分组把范围缩小到你要改的那几条。
            </Notification>
          ) : null}

          {snapshot.progress === null || busy !== 'apply' ? null : (
            <ProgressBar
              label="正在逐条写入并等待回读"
              value={snapshot.progress.done}
              max={snapshot.progress.total}
              valueText={`${snapshot.progress.done} / ${snapshot.progress.total}（已确认 ${snapshot.progress.applied}，失败 ${snapshot.progress.failed}，跳过 ${snapshot.progress.skipped}）`}
            />
          )}

          <Table
            aria-label="参数表"
            rows={shownRows}
            columns={columns}
            getRowKey={rowKey}
            defaultSort={{ key: 'name', direction: 'asc' }}
            density="compact"
            stickyHeader
            maxHeight={560}
            empty={
              snapshot.rows.length === 0
                ? {
                    title: downloading ? '正在读取参数表' : '还没有读到参数表',
                    description: downloading
                      ? '飞控会把整张参数表当成流推过来（真实机体有 1000~1500 条），收到第一条之后这里就会出现内容。'
                      : '点击"重新读取"向飞控请求整张参数表；收到之前这里没有数据，不是"机体没有参数"。',
                    level: downloading ? 'info' : 'caution',
                  }
                : {
                    title: '没有匹配的参数',
                    description: '当前搜索词或分组筛掉了全部条目。清空筛选即可回到完整列表。',
                  }
            }
          />
        </div>
      </Panel>
    </div>
  );
}

/*
 * 编辑器**不再自建弹窗宿主**。
 *
 * 这里曾经包一层 `SetupDialogs`（本分区自己实现的确认/对话框宿主）。统一弹窗系统
 * （`@hgc/ui` 的 `useConfirm` / `useDialog` + `<ConfirmDialogProvider>`）落地后，
 * 那层宿主既多余又危险：它会成为 `useConfirm()` 解析到的**最近**祖先，
 * 于是同一个应用里会有两处渲染弹窗的地方，"弹窗长得不一样"就从"不可能"变成"看你在哪一页"。
 * 现在整个应用只有应用根部那一处宿主，这个函数因此只剩一个身份：给外部调用点一个稳定名字。
 */
export function ParameterEditorScreen({ session }: ParameterEditorProps): JSX.Element {
  return <ParameterEditor session={session} />;
}

function text(value: number): string {
  return Number.isFinite(value) ? formatParamValue(value) : String(value);
}

function filterRows(
  snapshot: ParamSessionSnapshot,
  search: string,
  group: string,
  onlyModified: boolean,
): readonly ParamRow[] {
  const needle = search.trim().toUpperCase();
  return snapshot.rows.filter((row) => {
    if (group !== '' && (row.group ?? '') !== group) return false;
    if (onlyModified && row.pending === null && !row.differsFromReference) return false;
    if (needle === '') return true;
    return (
      row.name.toUpperCase().includes(needle) ||
      row.label.toUpperCase().includes(needle) ||
      row.groupLabel.toUpperCase().includes(needle) ||
      row.description.toUpperCase().includes(needle)
    );
  });
}

function ParamEditCell({
  row,
  busy,
  validate,
  onEdit,
  onRevert,
  onWrite,
}: {
  readonly row: ParamRow;
  readonly busy: boolean;
  readonly validate: (name: string, value: number) => ParamValidationResult;
  readonly onEdit: (name: string, value: number | null) => void;
  readonly onRevert: (name: string) => void;
  readonly onWrite: (row: ParamRow, event: MouseEvent<HTMLButtonElement>) => void;
}): JSX.Element {
  const requested = row.pending?.requested ?? row.value;

  /*
   * 范围与枚举只给**警告**，不给拒绝：ArduPilot 对越界值通常是钳制而不是拒绝，
   * 而上游的 Range 是给滑条用的建议区间，不完全等于固件的硬限制。
   * 唯一的硬判据永远是飞控回读的值（见 manager 的确认逻辑）。
   */
  const warning = useMemo(() => {
    const result = validate(row.name, requested);
    return (
      result.warnings.find((issue) =>
        ['below-min', 'above-max', 'not-in-enum', 'unknown-bit'].includes(issue.kind),
      ) ?? null
    );
  }, [requested, row.name, validate]);

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1">
        <div className="w-28">
          {/*
            刻意不把元数据的 min/max 交给 NumberField：那会让输入框在越界时自己变"非法"，
            而元数据的范围只是**建议区间**（固件的硬限制常常更宽），把越界画成错误会让人
            以为"这个值不能写"。越界由下面那行 caution 文字说明，写入按钮保持可用。
          */}
          <NumberField
            size="sm"
            value={requested}
            aria-label={`${row.name} 待写入值`}
            disabled={row.readOnly}
            onChange={(value) => onEdit(row.name, value)}
            {...(row.units === null ? {} : { unit: row.units })}
          />
        </div>
        <Button
          size="sm"
          variant="secondary"
          loading={busy}
          disabled={row.pending === null || row.readOnly}
          aria-label={`写入 ${row.name}`}
          onClick={(event) => onWrite(row, event)}
        >
          写入
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={row.pending === null}
          aria-label={`撤销 ${row.name} 的改动`}
          onClick={() => onRevert(row.name)}
        >
          撤销
        </Button>
      </div>

      {warning === null ? null : (
        <span data-param-warning="" className="text-[10px] leading-4 text-caution">
          {warning.message}
        </span>
      )}
    </div>
  );
}

function ParamStateCell({
  row,
  failure,
}: {
  readonly row: ParamRow;
  readonly failure: ParamWriteFailure | null;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {row.pending === null ? null : (
        <Badge size="sm" level="caution" dot>
          {`待写入：${text(row.pending.previous)} → ${text(row.pending.requested)}`}
        </Badge>
      )}
      {row.differsFromReference && row.reference !== null ? (
        <Badge size="sm" level="info" variant="outline">
          {`参考快照：${text(row.reference)}`}
        </Badge>
      ) : null}
      {row.readOnly ? (
        <Badge size="sm" level="warning">
          只读
        </Badge>
      ) : null}
      {row.rebootRequired ? (
        <Badge size="sm" level="caution" variant="outline">
          需重启生效
        </Badge>
      ) : null}
      {row.dangerous ? (
        <Badge size="sm" level="warning" variant="outline" dot>
          危险参数
        </Badge>
      ) : null}
      {failure === null ? null : (
        <Badge size="sm" level="critical" dot>
          {`写入未确认：${describeSetStatus(failure.status)}`}
        </Badge>
      )}
    </div>
  );
}

function ParamMetaCell({ row }: { readonly row: ParamRow }): JSX.Element {
  const enumText =
    row.enumValues === null
      ? null
      : row.enumValues.slice(0, 8).map((item) => `${item.value}=${item.label}`).join('，');

  return (
    <div className="flex max-w-xl flex-col gap-0.5">
      <span className="text-xs text-fg">
        {row.label === row.name ? null : <span className="text-fg-muted">{row.label} · </span>}
        {row.description === '' ? (
          <span className="text-fg-subtle">（没有说明文字）</span>
        ) : (
          row.description
        )}
      </span>
      <span className="text-[10px] leading-4 text-fg-subtle">
        {row.descriptionKind === 'en' ? '说明来自上游英文原文 · ' : ''}
        {row.range === null ? '取值无范围信息 · ' : `取值范围 ${text(row.range.low)} ~ ${text(row.range.high)} · `}
        {enumText === null ? '' : `枚举 ${enumText}`}
      </span>
    </div>
  );
}

function MetadataNotice({
  session,
  snapshot,
}: {
  readonly session: ParameterSession;
  readonly snapshot: ParamSessionSnapshot;
}): JSX.Element | null {
  const metadata = snapshot.metadata;
  if (metadata.loading) {
    return (
      <Notification level="info" title="正在获取参数元数据（中文说明、单位、取值范围）">
        元数据没到之前参数照常可用，只是少了说明与范围提示。
      </Notification>
    );
  }
  if (metadata.status !== 'empty') return null;

  return (
    <Notification
      level="warning"
      title="拿不到参数元数据：这一页只有参数名与值，没有中文说明和取值范围"
      actions={
        <Button size="sm" variant="secondary" onClick={() => void session.loadMetadata(true)}>
          重试获取元数据
        </Button>
      }
    >
      {metadata.reason ?? '元数据来源不可用'}。参数照常下载、照常读写 —— 元数据只影响说明与提示。
    </Notification>
  );
}

function DownloadNotice({
  snapshot,
  onCancel,
}: {
  readonly snapshot: ParamSessionSnapshot;
  readonly onCancel: () => void;
}): JSX.Element | null {
  const download = snapshot.download;
  if (download === null) return null;

  if (download.phase === 'requesting' || download.phase === 'downloading' || download.phase === 'retrying') {
    return (
      <div className="flex flex-col gap-1">
        <ProgressBar
          level="info"
          label={
            download.phase === 'retrying'
              ? '有参数没收到，正在按编号补缺'
              : '正在读取参数表'
          }
          value={download.total === null ? null : download.received}
          max={download.total ?? 1}
          valueText={`已收到 ${download.received} 条${download.total === null ? '' : ` / 共 ${download.total} 条`}`}
        />
        <div>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            取消读取
          </Button>
        </div>
      </div>
    );
  }

  const last = snapshot.lastDownload;
  if (last === null) return null;

  if (last.status === 'partial') {
    return (
      <Notification
        level="caution"
        title={`参数表不完整：有 ${last.missing.length} 条没收到`}
      >
        缺口编号：{last.missing.slice(0, 12).join('、')}
        {last.missing.length > 12 ? ` …（共 ${last.missing.length} 个）` : ''}。
        缺失的参数**不会**出现在下面这张表里，所以"没看到"不等于"没有这个参数"；请先重新读取。
      </Notification>
    );
  }
  if (last.status === 'failed') {
    return (
      <Notification level="critical" title="参数表读取失败">
        {last.reason ?? '没有拿到原因'}（已收到 {last.received} 条）。参数表不完整时，
        改参数前请先确认你要改的那条确实在读到的列表里。
      </Notification>
    );
  }
  return null;
}

function FailureNotice({
  failure,
  onDismiss,
}: {
  readonly failure: ParamWriteFailure;
  readonly onDismiss: () => void;
}): JSX.Element {
  const readOnly = failure.status === 'read-only';
  return (
    <Notification
      level={readOnly ? 'warning' : 'critical'}
      title={
        readOnly
          ? `${failure.name} 未写入：固件里是只读参数`
          : `${failure.name} 写入未确认：${describeSetStatus(failure.status)}`
      }
      onDismiss={onDismiss}
      dismissLabel={`关闭 ${failure.name} 的写入提示`}
    >
      <p className="m-0">{failure.message}</p>
      <p className={`m-0 mt-1 font-medium ${NOTICE_TEXT}`}>
        {failure.actual === null
          ? '没有收到任何回读值 —— 这次写入的结果未知，不要当成已生效。'
          : `实际回读值：${text(failure.actual)}（这是飞控当前的值，也是最终生效的值）`}
      </p>
    </Notification>
  );
}

function PendingDetailTable({ session }: { readonly session: ParameterSession }): JSX.Element {
  const snapshot = useParameterSessionSnapshot(session);
  if (snapshot.pending.length === 0) {
    return (
      <EmptyState
        compact
        title="没有待写入的改动"
        description="在参数表里改一个值就会出现在这里。"
      />
    );
  }
  return (
    <table className="w-full border-collapse text-xs" aria-label="待写入改动明细">
      <thead>
        <tr>
          <th scope="col" className="border-b border-line px-2 py-1 text-left text-fg-muted">
            参数
          </th>
          <th scope="col" className="border-b border-line px-2 py-1 text-right text-fg-muted">
            当前值
          </th>
          <th scope="col" className="border-b border-line px-2 py-1 text-right text-fg-muted">
            将写入
          </th>
          <th scope="col" className="border-b border-line px-2 py-1 text-left text-fg-muted">
            风险
          </th>
        </tr>
      </thead>
      <tbody>
        {snapshot.pending.map((edit) => (
          <tr key={edit.name}>
            <td className="border-b border-line px-2 py-1 font-mono">{edit.name}</td>
            <td className="border-b border-line px-2 py-1 text-right tabular-nums">
              {text(edit.previous)}
            </td>
            <td className="border-b border-line px-2 py-1 text-right tabular-nums">
              {text(edit.requested)}
            </td>
            <td className="border-b border-line px-2 py-1">
              {session.isDangerous(edit.name) ? (
                <span className="text-warning">危险：{session.dangerReason(edit.name)}</span>
              ) : (
                <span className="text-fg-subtle">普通</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
